import { ORDER_TYPE } from '@exam-management/common/utils';
import { FilterOperator, QSetStatus, useGetReviewedQSetsQuery } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useNavigate, useParams } from '@exam-management/router';
import { Flex, type GridCellParams, type GridRowSelectionModel } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useMemo } from 'react';

import { BaseQSetList } from '../../components';
import { useQSetListSearchParams } from '../../hooks';
import { useQSetListModal } from '../../providers';

import { columns } from './columns';
import ReviewedQSetListNoRowsOverlay from './reviewed-qset-list-no-rows-overlay';

export default function ReviewedQSetList() {
  const { courseId } = useParams('/exam-courses/:courseId/qsets');
  const navigate = useNavigate();
  const isPublishable = useHasPermission(APP_PERMISSIONS.EXAM.QSET.STATUS);
  const { onSelectQSetIds } = useQSetListModal();
  const [
    { page, pageSize, sortOrder, sortBy },
    { changePageNumber, changePageSize, changeSorting },
  ] = useQSetListSearchParams();

  const { data: reviewedQSetsResult, loading: isSearchQSetsLoading } = useGetReviewedQSetsQuery({
    variables: {
      searchQSetsInput: {
        paging: {
          pageSize: pageSize,
          currentPage: page,
        },
        order: {
          type: ORDER_TYPE[sortOrder],
          field: sortBy,
        },
        filter: {
          conditions: [
            {
              value: courseId,
              operator: FilterOperator.Equal,
              field: 'examCourseId',
            },
            {
              value: QSetStatus.Reviewed,
              operator: FilterOperator.Equal,
              field: 'status',
            },
          ],
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const totalQSets = reviewedQSetsResult?.excSearchQSets.total ?? 0;
  const qSets = useMemo(
    () =>
      (reviewedQSetsResult?.excSearchQSets.qSets ?? []).map((qSet) => ({
        id: qSet.id,
        code: qSet.code,
        status: qSet.status,
        updatedAt: new Date(qSet.updatedAt),
        lastReviewedAt: qSet.lastReviewedAt ? new Date(qSet.lastReviewedAt) : null,
        reviewers: qSet.assignees,
        qConfig: {
          code: qSet.qConfig.code,
          name: qSet.qConfig.name,
        },
      })),
    [reviewedQSetsResult?.excSearchQSets.qSets],
  );

  const handleCellClick = (cell: GridCellParams) => {
    if (!['moreAction', '__check__'].includes(cell.field)) {
      navigate('/exam-courses/:courseId/qsets/:qSetId', {
        params: {
          courseId,
          qSetId: cell.row.id,
        },
      });
    }
  };

  const handleRowSelectionModelChange = (selection: GridRowSelectionModel) => {
    onSelectQSetIds(selection.map(String));
  };

  return (
    <Flex sx={{ flexDirection: 'column', flex: 1 }}>
      <BaseQSetList
        columns={columns}
        rows={qSets}
        getRowHeight={() => 'auto'}
        hideFooter={totalQSets === 0}
        loading={isSearchQSetsLoading}
        checkboxSelection={isPublishable}
        onCellClick={handleCellClick}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        keepNonExistentRowsSelected
        slots={{
          noRowsOverlay: ReviewedQSetListNoRowsOverlay,
        }}
        slotProps={{
          footer: {
            total: totalQSets,
            pagination: {
              page: page,
              count: totalQSets,
              rowsPerPage: pageSize,
              onPageChange: (_, page) => {
                changePageNumber(page);
              },
              onRowsPerPageChange: (event) => {
                changePageSize(parseInt(event.target.value));
              },
            },
          },
        }}
        onSortModelChange={(sorting) => {
          const sortModel = sorting[0];

          if (sortModel?.sort && sortModel?.field) {
            return changeSorting({
              sortOrder: sortModel.sort,
              sortBy: sortModel.field,
            });
          }
          // Reset sorting to default
          return changeSorting({
            sortOrder: null,
            sortBy: null,
          });
        }}
      />
    </Flex>
  );
}
